import type { EntityCategory } from '@dealstack/shared/lib/dealstackAi/entities';
import type { ReasonCode, ReasonSeverity } from '@dealstack/shared/lib/dealstackAi/reasonCodes';

/** A reason code's metadata. No copy: the UI maps each code to its Tolgee key. */
interface ReasonCodeMetadata<TCode extends string> {
	code: TCode;
	/** The code's own severity, before row derivation raises or lowers it for the row's state and decision. */
	severity: ReasonSeverity;
	/**
	 * The drawer whose resolution clears the code; null when the fix is not an entity resolution, or when it depends
	 * on the entity (`entity_unresolved`, `entity_removed`: row derivation sets the blocker's category).
	 */
	resolveIn: EntityCategory | null;
}

type ReasonCodeMetadataMap<TCode extends string> = {
	readonly [C in TCode]: ReasonCodeMetadata<C>;
};

/** Metadata for every row reason code. */
export const REASON_CODE_METADATA: ReasonCodeMetadataMap<ReasonCode> = {
	holder_missing: { code: 'holder_missing', severity: 'hard', resolveIn: null },
	units_missing: { code: 'units_missing', severity: 'hard', resolveIn: null },
	security_missing: { code: 'security_missing', severity: 'hard', resolveIn: null },
	seller_missing: { code: 'seller_missing', severity: 'hard', resolveIn: null },
	units_not_positive: { code: 'units_not_positive', severity: 'hard', resolveIn: null },

	near_existing: { code: 'near_existing', severity: 'soft', resolveIn: null },
	cost_mismatch: { code: 'cost_mismatch', severity: 'soft', resolveIn: null },
	number_not_verbatim: { code: 'number_not_verbatim', severity: 'soft', resolveIn: null },
	quote_unverified: { code: 'quote_unverified', severity: 'soft', resolveIn: null },
	image_only_evidence: { code: 'image_only_evidence', severity: 'soft', resolveIn: null },
	type_unknown: { code: 'type_unknown', severity: 'soft', resolveIn: null },
	loan_terms: { code: 'loan_terms', severity: 'soft', resolveIn: null },
	nominee_roles: { code: 'nominee_roles', severity: 'soft', resolveIn: null },
	disposal_unfolded: { code: 'disposal_unfolded', severity: 'soft', resolveIn: null },
	quote_not_verbatim: { code: 'quote_not_verbatim', severity: 'soft', resolveIn: null },
	document_not_evidence: { code: 'document_not_evidence', severity: 'soft', resolveIn: null },

	date_assumed: { code: 'date_assumed', severity: 'info', resolveIn: null },
	date_missing: { code: 'date_missing', severity: 'blocking', resolveIn: null },
	dates_differ: { code: 'dates_differ', severity: 'info', resolveIn: null },
	date_signing_stamp: { code: 'date_signing_stamp', severity: 'info', resolveIn: null },
	date_from_sibling: { code: 'date_from_sibling', severity: 'info', resolveIn: null },
	date_entered: { code: 'date_entered', severity: 'info', resolveIn: null },
	conversion_target_missing: {
		code: 'conversion_target_missing',
		severity: 'blocking',
		resolveIn: 'securities',
	},
	fx_at_document_date: { code: 'fx_at_document_date', severity: 'info', resolveIn: null },
	fx_no_date: { code: 'fx_no_date', severity: 'info', resolveIn: null },
	fx_overridden: { code: 'fx_overridden', severity: 'info', resolveIn: null },
	fx_rate_missing: { code: 'fx_rate_missing', severity: 'blocking', resolveIn: null },
	currency_ambiguous: {
		code: 'currency_ambiguous',
		severity: 'blocking',
		resolveIn: 'currencies',
	},
	entity_unresolved: { code: 'entity_unresolved', severity: 'blocking', resolveIn: null },
	entity_removed: { code: 'entity_removed', severity: 'blocking', resolveIn: null },
	issuer_inferred: { code: 'issuer_inferred', severity: 'info', resolveIn: null },
	duplicate_disagrees: { code: 'duplicate_disagrees', severity: 'soft', resolveIn: null },
	holder_pending: { code: 'holder_pending', severity: 'blocking', resolveIn: 'people' },
	ledger_twins: { code: 'ledger_twins', severity: 'soft', resolveIn: null },
	ledger_exact_tie: { code: 'ledger_exact_tie', severity: 'soft', resolveIn: null },
	likely_duplicate: { code: 'likely_duplicate', severity: 'blocking', resolveIn: null },
	type_unconfirmed: { code: 'type_unconfirmed', severity: 'soft', resolveIn: null },
	fold_invalid: { code: 'fold_invalid', severity: 'soft', resolveIn: null },
	convention_unconfirmed: {
		code: 'convention_unconfirmed',
		severity: 'blocking',
		resolveIn: null,
	},
	partial_interest_transfer: {
		code: 'partial_interest_transfer',
		severity: 'blocking',
		resolveIn: null,
	},
	nominee_split_title: { code: 'nominee_split_title', severity: 'blocking', resolveIn: null },
	nominee_vehicle_row: { code: 'nominee_vehicle_row', severity: 'blocking', resolveIn: null },
	nominee_unattributed: { code: 'nominee_unattributed', severity: 'blocking', resolveIn: null },
	awaiting_information: { code: 'awaiting_information', severity: 'blocking', resolveIn: null },
	cost_missing: { code: 'cost_missing', severity: 'blocking', resolveIn: null },
};
